import { createSelector } from "reselect";
import { SimulationFormFields } from "pages/RS-Workspace/components/Scenario/components/SimulationDialog";
import { bioreactorsListSelector, processVariablesOptionsSelector, processVariablesSelector, } from "store/global/global.selectors";
import { fullyConfiguredCellModelsSelector } from "store/cell-model-list/cell-model-list.selectors";
import { fullyConfiguredRecipesSelector } from "store/recipe-list/recipe-list.selectors";
import { simulationPreviewDefaultSelectedVariables } from "../rs-workspace.store";
import { calculateAvailableHeight, calculateHighlightedElementIds, calculateScenarioCellModelsRecipesSectionHeight, composeSimulationErrorMessage, defaultInputSetInput, getRecipesCustomVariables, mergeSpaceviewerResults, positionScenarioCanvasElements, positionScenarioCellModels, positionScenarioRecipes, prepareCurrentSimulationPreviewLinesData, prepareGroupedVariablesList, prepareSimulationPreviewExportedData, SECTION_SUBHEADER_HEIGHT, setupAlternativeStepOptimizationParameter, } from "../utils/scenario.utils";
import { InsightInputType, InsightOutputTypes, InsightOutputVariableTypes, OptimizationParameterParameterType, OptimizationTargetType, ScenarioNodeType, } from "../rs-workspace.types";
import { CutType, GlobalEntityType, SpaceviewerAxisType, } from "shared/types";
import { InsightFormFields } from "pages/RS-Workspace/components/Scenario/components/InsightDialog";
import { calculateInputSetFinalScore, getAssessmentUsedRecipeIds, getDefaultOutput, getInsightInputSetOptions, getInsightLatestScoreData, getInsightOutputOptions, getInsightsScoresCalculationRequests, isVariantUsedInInsight, } from "../utils/insights.utils";
import { isNotEmpty } from "utils/validate";
import { N_A, timeUnits } from "shared/rsDictionary";
import { InvokeRsSocketMethods } from "api/rs-socket-callbacks";
import { processDataListSelector } from "store/process-data-list/process-data-list.selectors";
import { getCustomVariableOptions } from "store/recipe/custom-variables.utils";
import { isInsightCompleted, isSimulationCompleted, isSimulationVariantCompleted, } from "../utils/completeness.utils";
import { checkRefinementIsCalculated, getSelectedCellModelParameterToOptimize, } from "store/cell-model/cell-model.utils";
import { flatSteps, getAlternativeStepFlowName, getSelectedRecipeParameterToOptimize, } from "../../recipe/recipe.utils";
import { getOptimizationParameterSectionTarget, getOptimizationParameterSectionTargetSource, getSelectedParameterToOptimize, MIN_FLOWS_QUANTITY, } from "../utils/common.utils";
import { isSimulationVariantUsedInResultElement } from "../utils/results.utils";
export const OUTPUT_FINAL_ID = -1;
export const getWorkspaceState = (state) => state.rsWorkspace;
export const getWorkspaceStateSelector = createSelector(getWorkspaceState, (state) => state.state);
export const displayModeSelector = createSelector(getWorkspaceState, (state) => state.mode);
export const previewRequestsSelector = createSelector(getWorkspaceState, (state) => state.previewRequests);
export const workspaceCellModelsSelector = createSelector(getWorkspaceStateSelector, (state) => state?.cellModels || []);
export const workspaceNotRefinedCellModelsSelector = createSelector(getWorkspaceStateSelector, (state) => (state?.cellModels || []).filter((cellModel) => !cellModel?.refined));
export const workspaceRecipesSelector = createSelector(getWorkspaceStateSelector, (state) => state?.recipes || []);
export const workspaceSimulationsSelector = createSelector(getWorkspaceStateSelector, (state) => state?.simulations || []);
export const workspaceOptimizationsSelector = createSelector(getWorkspaceStateSelector, (state) => state?.optimizations || []);
export const workspaceProcessDataSelector = createSelector(getWorkspaceStateSelector, (state) => state?.processData || []);
export const optimizationsByInsightIdSelector = (insightId) => createSelector(workspaceOptimizationsSelector, (optimizations) => optimizations.filter((optimization) => optimization.state.input?.insightId === insightId));
export const workspaceInsightsSelector = createSelector(getWorkspaceStateSelector, (state) => state?.insights || []);
export const insightEvaluationOperationsSelector = createSelector(getWorkspaceState, (state) => state.insightOperations.evaluationOperations);
export const insightComparisonOperationsSelector = createSelector(getWorkspaceState, (state) => state.insightOperations.comparisonOperations);
export const insightTransformationsSelector = createSelector(getWorkspaceState, (state) => state.insightTransformations);
export const insightEssentialCombinationsSelector = createSelector(getWorkspaceState, (state) => state.insightEssentialCombinations);
export const insightOperationDescriptionsSelector = createSelector(getWorkspaceState, (state) => state.insightOperationDescriptions);
export const workspaceCurrentSimulationIdSelector = createSelector(getWorkspaceState, (state) => state.currentSimulationId);
export const workspaceCurrentVariantIdSelector = createSelector(getWorkspaceState, (state) => state.currentVariantId);
export const renameVariantDialogOpenSelector = createSelector(getWorkspaceState, (state) => state.renameVariantDialogOpen);
export const insightDefaultNameSelector = createSelector(getWorkspaceState, (state) => state.insightDefaultName);
export const unsavedChangesCalculationDialogOpenSelector = createSelector(getWorkspaceState, (state) => state.unsavedChangesCalculationDialogOpen);
export const workspaceCurrentInsightIdSelector = createSelector(getWorkspaceState, (state) => state.currentInsightId);
export const isHighlightModeActiveSelector = createSelector(getWorkspaceState, (state) => state.isHighlightModeActive);
export const highlightLeadingNodeSelector = createSelector(getWorkspaceState, (state) => state.highlightLeadingNode);
export const highlightedElementIdsSelector = createSelector(workspaceSimulationsSelector, workspaceProcessDataSelector, workspaceInsightsSelector, workspaceOptimizationsSelector, highlightLeadingNodeSelector, (simulations, processData, insights, optimizations, highlightLeadingNode) => calculateHighlightedElementIds(simulations, processData, insights, optimizations, highlightLeadingNode));
export const persistedInsightsDataSelector = createSelector(getWorkspaceState, (state) => state.persistedInsightsData.filter((item) => !item.isOriginal));
export const recipeCustomVariablesSelector = (recipeIds) => createSelector(workspaceRecipesSelector, (recipes) => getRecipesCustomVariables(recipes, recipeIds));
export const insightExpandedInputSetIndexSelector = createSelector(workspaceCurrentInsightIdSelector, persistedInsightsDataSelector, (currentInsightId, insightsData) => {
    const insightEntity = getInsightLatestScoreData(insightsData, currentInsightId);
    return insightEntity?.expandedInputSetIndex ?? 0;
});
export const workspaceCurrentInsightSelector = createSelector(workspaceInsightsSelector, workspaceCurrentInsightIdSelector, (insights, currentInsightId) => insights.find(({ id }) => id === currentInsightId));
export const persistedSimulationPreviewsSelector = createSelector(getWorkspaceState, (state) => state.simulationPreviews.persisted);
export const temporarySimulationPreviewsSelector = createSelector(getWorkspaceState, (state) => state.simulationPreviews.temporary);
export const incompleteSimulationsIdsSelector = createSelector(workspaceCellModelsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, (cellModels, recipes, simulations) => {
    return simulations.reduce((acc, item) => {
        if (!isSimulationCompleted(item, cellModels, recipes)) {
            acc.push(item.id);
        }
        return [...acc];
    }, []);
});
export const insightInputSetInputSimulationOptionsSelector = createSelector(workspaceSimulationsSelector, workspaceCurrentInsightSelector, workspaceCellModelsSelector, workspaceRecipesSelector, incompleteSimulationsIdsSelector, (simulations, currentInsight, cellModels, recipes, incompleteSimulationsIds) => simulations
    .filter((simulation) => {
    const inputSets = currentInsight ? currentInsight.state.input.inputSets : [];
    const simulationUsedInInsight = inputSets.some((inputSet) => inputSet.inputs.some((input) => input.inputType === InsightInputType.SIMULATION && input.inputId === simulation.id));
    return simulationUsedInInsight || isSimulationCompleted(simulation, cellModels, recipes);
})
    .map((simulation) => ({
    value: simulation.id,
    label: simulation.name,
    groupId: InsightInputType.SIMULATION,
    disabled: incompleteSimulationsIds.includes(simulation.id),
})));
export const insightInputSetInputProcessDataOptionsSelector = createSelector(workspaceProcessDataSelector, (processData) => processData.map((processDataItem) => ({
    value: processDataItem.id,
    label: processDataItem.name,
    groupId: InsightInputType.PROCESS_DATA,
})));
export const insightInputSetInputOptionsSelector = createSelector(insightInputSetInputSimulationOptionsSelector, insightInputSetInputProcessDataOptionsSelector, (simulationOptions, processDataOptions) => {
    let data = [];
    if (simulationOptions.length) {
        data = [
            {
                value: InsightInputType.SIMULATION,
                label: "Simulations",
                disabled: true,
                isGroupName: true,
                groupId: InsightInputType.SIMULATION,
            },
            ...simulationOptions,
        ];
    }
    if (processDataOptions.length) {
        data = [
            ...data,
            {
                value: InsightInputType.PROCESS_DATA,
                label: "Process Data",
                disabled: true,
                isGroupName: true,
                groupId: InsightInputType.PROCESS_DATA,
            },
            ...processDataOptions,
        ];
    }
    return data;
});
export const insightOutputVariableOptionsSelector = createSelector(processVariablesOptionsSelector, (processVariables) => processVariables.reduce((acc, option) => option.category === "RecipeVariables"
    ? acc
    : [...acc, { ...option, type: InsightOutputVariableTypes.CellModel }], []));
export const completedWorkspaceSimulationsOptionsSelector = (simulationId) => createSelector(workspaceSimulationsSelector, workspaceCellModelsSelector, workspaceRecipesSelector, (simulations, cellModels, recipes) => simulations.reduce((acc, simulation) => {
    const isConfigured = isSimulationCompleted(simulation, cellModels, recipes);
    const currentSimulation = simulationId === simulation.id;
    if (isConfigured || currentSimulation) {
        acc.push({
            value: simulation.id,
            label: simulation.name,
            disabled: !isConfigured && currentSimulation,
        });
    }
    return acc;
}, []));
export const completedWorkspaceInsightsOptionsSelector = (insightId) => createSelector(workspaceInsightsSelector, processVariablesSelector, incompleteSimulationsIdsSelector, workspaceSimulationsSelector, workspaceCellModelsSelector, workspaceRecipesSelector, (insights, processVariables, incompleteSimulationIds, simulations, cellModels, recipes) => insights.reduce((acc, { id, name, state }) => {
    const isCompleted = isInsightCompleted(state, processVariables, incompleteSimulationIds, simulations, cellModels, recipes);
    if (isCompleted || id === insightId) {
        acc.push({
            value: id,
            label: name,
            disabled: !isCompleted && id === insightId,
        });
    }
    return acc;
}, []));
export const insightOutputOperationOptionsSelector = (selectedTransformation, evaluationType) => createSelector(insightEvaluationOperationsSelector, insightComparisonOperationsSelector, insightOperationDescriptionsSelector, (evaluationOperations, comparisonOperations, descriptionsList) => {
    let finalOperations = evaluationType === InsightOutputTypes.Evaluation ? evaluationOperations : comparisonOperations;
    const descriptions = evaluationType === InsightOutputTypes.Evaluation
        ? descriptionsList.evaluationDescriptions
        : descriptionsList.comparisonDescriptions;
    if (selectedTransformation !== null && descriptions !== null) {
        finalOperations = finalOperations.filter(({ type }) => !!descriptions?.[type]?.[selectedTransformation.operationType.value]);
    }
    return finalOperations.map((item) => ({ value: item.type, label: item.name }));
});
export const insightOutputTransformationsOptionsSelector = (selectedOperation, evaluationType) => createSelector(insightTransformationsSelector, insightOperationDescriptionsSelector, (transformations, descriptionsList) => {
    let finalTransformations = transformations.filter(({ operationType }) => operationType !== CutType.PointInTime);
    const descriptions = evaluationType === InsightOutputTypes.Evaluation
        ? descriptionsList.evaluationDescriptions
        : descriptionsList.comparisonDescriptions;
    if (selectedOperation !== null && descriptions !== null) {
        finalTransformations = finalTransformations.filter(({ operationType }) => !!descriptions?.[selectedOperation.type.value][operationType]);
    }
    return finalTransformations.map((item) => ({ value: item.operationType, label: item.name }));
});
export const insightByIdSelector = (insightId) => createSelector(workspaceInsightsSelector, (insights) => insights.find(({ id }) => id === insightId));
export const insightErrorsSelector = createSelector(getWorkspaceState, (state) => state.insightErrors);
export const insightDialogInitialValuesSelector = createSelector(insightDefaultNameSelector, workspaceCurrentInsightSelector, insightInputSetInputOptionsSelector, insightOutputVariableOptionsSelector, insightTransformationsSelector, processVariablesSelector, workspaceRecipesSelector, workspaceCellModelsSelector, workspaceSimulationsSelector, insightExpandedInputSetIndexSelector, incompleteSimulationsIdsSelector, (insightDefaultName, currentInsight, inputOptions, variableOptions, transformationsList, processVariables, recipes, cellModels, simulations, expandedInputSetIndex, incompleteSimulationsIds) => {
    const insightState = currentInsight?.state ?? {};
    const inputSets = currentInsight
        ? insightState.input.inputSets.map((inputSet) => ({
            [InsightFormFields.id]: inputSet.id,
            [InsightFormFields.inputs]: inputSet[InsightFormFields.inputs].map((input) => ({
                ...input,
                [InsightFormFields.inputId]: inputOptions.find((item) => item.value === input.inputId && item.groupId === input.inputType) ??
                    null,
            })),
        }))
        : [{ [InsightFormFields.id]: 1, inputs: [defaultInputSetInput] }];
    const expandedInputSet = inputSets[expandedInputSetIndex];
    const outputs = Array(3)
        .fill(getDefaultOutput())
        .map((output, index) => {
        const outputId = index + 1;
        let outputData = { ...output, [InsightFormFields.id]: outputId };
        const existingOutput = insightState?.outputs?.find((item) => item.id === outputId);
        if (!!currentInsight && existingOutput) {
            let processVariable = null;
            let operation = null;
            let transformationOperation = null;
            if (existingOutput.processVariable !== null) {
                const usedRecipeIds = getAssessmentUsedRecipeIds(simulations, recipes, expandedInputSet?.inputs, existingOutput?.type, existingOutput?.sourceA, existingOutput?.sourceB);
                const customVariablesOptions = getCustomVariableOptions(usedRecipeIds, recipes);
                if (existingOutput.processVariable?.variableType) {
                    processVariable = variableOptions.find((item) => item.value === existingOutput.processVariable?.variableType);
                }
                if (existingOutput.processVariable?.customVariableId) {
                    processVariable = customVariablesOptions.find((item) => item.value === existingOutput.processVariable?.customVariableId &&
                        usedRecipeIds.includes(item.recipeId));
                }
            }
            if (existingOutput.operation !== null) {
                operation = {
                    [InsightFormFields.type]: {
                        value: existingOutput.operation?.type,
                        label: existingOutput.operation?.name,
                    },
                    [InsightFormFields.parameters]: existingOutput.operation?.parameters,
                    [InsightFormFields.preProcessingEnabled]: existingOutput.operation?.preProcessingEnabled,
                    [InsightFormFields.preProcessingType]: existingOutput.operation?.preProcessingType,
                    ...(existingOutput.operation.timeWindow
                        ? {
                            [InsightFormFields.timeWindow]: {
                                value: existingOutput.operation.timeWindow.value,
                                units: {
                                    value: existingOutput.operation.timeWindow.units,
                                    label: timeUnits.find((option) => option.value === existingOutput?.operation?.timeWindow?.units)?.label,
                                },
                            },
                        }
                        : {}),
                };
            }
            if (existingOutput.transformationOperation !== null) {
                const transformationType = transformationsList.find((item) => item.operationType === existingOutput.transformationOperation?.operationType);
                transformationOperation = {
                    [InsightFormFields.operationType]: {
                        value: transformationType?.operationType,
                        label: transformationType?.name,
                    },
                    parameter: null,
                };
            }
            outputData = {
                ...existingOutput,
                [InsightFormFields.sourceA]: isNotEmpty(existingOutput.sourceA)
                    ? {
                        value: existingOutput.sourceA,
                        label: `Input ${existingOutput.sourceA}`,
                    }
                    : null,
                ...(existingOutput.sourceB
                    ? {
                        [InsightFormFields.sourceB]: {
                            value: existingOutput.sourceB,
                            label: `Input ${existingOutput.sourceB}`,
                        },
                    }
                    : {}),
                [InsightFormFields.processVariable]: processVariable,
                [InsightFormFields.operation]: operation,
                [InsightFormFields.transformationOperation]: transformationOperation,
            };
        }
        return outputData;
    });
    return {
        [InsightFormFields.name]: currentInsight ? currentInsight.name : insightDefaultName,
        [InsightFormFields.inputCount]: insightState?.input?.inputCount,
        [InsightFormFields.inputSets]: inputSets,
        [InsightFormFields.outputs]: outputs,
        [InsightFormFields.isComplete]: isInsightCompleted(insightState, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes) ?? false,
    };
});
export const preloadInsightsScoresPayloadSelector = createSelector(workspaceInsightsSelector, processVariablesSelector, incompleteSimulationsIdsSelector, workspaceSimulationsSelector, workspaceCellModelsSelector, workspaceRecipesSelector, (insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes) => getInsightsScoresCalculationRequests(insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes));
export const optimizationDefaultNameSelector = createSelector(getWorkspaceState, (state) => state.optimizationDefaultName);
export const workspaceCurrentOptimizationIdSelector = createSelector(getWorkspaceState, (state) => state.currentOptimizationId);
export const workspaceCurrentOptimizationSelector = createSelector(workspaceOptimizationsSelector, workspaceCurrentOptimizationIdSelector, (optimizations, currentOptimizationId) => optimizations.find(({ id }) => id === currentOptimizationId));
export const optimizationByIdSelector = (optimizationId) => createSelector(workspaceOptimizationsSelector, (optimizations) => optimizations.find(({ id }) => id === optimizationId));
export const optimizationDialogInitialValuesSelector = createSelector(optimizationDefaultNameSelector, workspaceCurrentOptimizationSelector, workspaceInsightsSelector, workspaceSimulationsSelector, workspaceRecipesSelector, workspaceCellModelsSelector, (optimizationDefaultName, currentOptimization, workspaceInsights, workspaceSimulations, workspaceRecipes, workspaceCellModels) => {
    const name = currentOptimization ? currentOptimization.name : optimizationDefaultName;
    let state = currentOptimization ? currentOptimization.state : {};
    if (currentOptimization?.state.input?.optimizationTarget === OptimizationTargetType.RECIPE) {
        const { insightId, insightInputSetId } = currentOptimization.state.input;
        const selectedInsight = workspaceInsights.find(({ id }) => id === insightId);
        state = {
            ...currentOptimization?.state,
            parameters: currentOptimization?.state.parameters?.map((parametersSection) => {
                const selectedInput = selectedInsight?.state?.input?.inputSets
                    ?.find(({ id }) => id === insightInputSetId)
                    ?.inputs?.find(({ id }) => id === parametersSection.inputId);
                const targetSource = getOptimizationParameterSectionTargetSource(selectedInput, workspaceSimulations);
                const target = getOptimizationParameterSectionTarget(workspaceRecipes, workspaceCellModels, targetSource, OptimizationTargetType.RECIPE);
                return {
                    ...parametersSection,
                    parameters: parametersSection.parameters?.map((parameter) => parameter.type === OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE
                        ? parameter
                        : setupAlternativeStepOptimizationParameter(parameter, target)),
                };
            }),
        };
    }
    return {
        id: currentOptimization?.id,
        state,
        name,
    };
});
export const simulationPreviewErroredSelector = (simulationId, persisted) => createSelector(persistedSimulationPreviewsSelector, temporarySimulationPreviewsSelector, workspaceCurrentVariantIdSelector, (persistedPreviews, temporaryPreviews, variantId) => {
    const temporaryPreviewData = temporaryPreviews?.[`${simulationId}_${variantId}`];
    const persistedPreviewData = persistedPreviews[`${simulationId}_${variantId}`];
    if (persisted && isNotEmpty(simulationId)) {
        return !!persistedPreviewData?.graphData?.errorMessage;
    }
    return temporaryPreviews === null ? false : !!temporaryPreviewData?.graphData?.errorMessage;
});
export const workspaceCurrentSimulationSelector = createSelector(workspaceCurrentSimulationIdSelector, workspaceSimulationsSelector, (currentSimulationId, simulations) => simulations.find(({ id }) => id === currentSimulationId));
export const insightsBySimulationIdSelector = (simulationId) => createSelector(workspaceInsightsSelector, (insights) => {
    return insights.reduce((acc, insight) => {
        const { inputSets } = insight.state.input;
        for (const inputSet of inputSets) {
            const relatedInputs = inputSet.inputs.find((input) => input.inputId == simulationId && input.inputType == InsightInputType.SIMULATION);
            if (relatedInputs) {
                acc.push(relatedInputs);
            }
        }
        return acc;
    }, []);
});
export const simulationPreviewDataSelector = createSelector(workspaceCurrentSimulationIdSelector, workspaceCurrentVariantIdSelector, temporarySimulationPreviewsSelector, (simulationId, variantId, temporarySimulationPreview) => {
    if (temporarySimulationPreview === null) {
        return {};
    }
    const previewData = temporarySimulationPreview[`${simulationId ?? -1}_${variantId}`];
    const selectedVariables = previewData?.selectedVariables || simulationPreviewDefaultSelectedVariables;
    const variablesList = prepareGroupedVariablesList(previewData?.graphData?.data || []);
    const { lines, unit } = prepareCurrentSimulationPreviewLinesData(selectedVariables, previewData?.graphData?.data);
    const serverError = composeSimulationErrorMessage(previewData?.graphData?.error?.errors[0]);
    return {
        lines,
        unit,
        selectedVariables,
        variablesList,
        serverError,
    };
});
export const simulationSocketErrorSelector = createSelector(workspaceCurrentSimulationIdSelector, workspaceCurrentVariantIdSelector, temporarySimulationPreviewsSelector, (simulationId, variantId, temporarySimulationPreviews) => {
    if (temporarySimulationPreviews === null)
        return null;
    const previewData = temporarySimulationPreviews[`${simulationId ?? -1}_${variantId}`];
    return previewData?.socketError;
});
export const currentSimulationExportDataSelector = createSelector(workspaceCurrentSimulationIdSelector, workspaceCurrentVariantIdSelector, temporarySimulationPreviewsSelector, (simulationId, variantId, temporarySimulationPreview) => {
    if (temporarySimulationPreview === null)
        return [];
    const previewData = temporarySimulationPreview[`${simulationId ?? -1}_${variantId}`];
    return prepareSimulationPreviewExportedData(previewData);
});
export const insightsByProcessDataIdSelector = (processDataId) => createSelector(workspaceInsightsSelector, (insights) => {
    return insights.reduce((acc, insight) => {
        const { inputSets } = insight.state.input;
        for (const inputSet of inputSets) {
            const relatedInputs = inputSet.inputs.find((input) => input.inputId == processDataId && input.inputType == InsightInputType.PROCESS_DATA);
            if (relatedInputs) {
                acc.push(relatedInputs);
            }
        }
        return acc;
    }, []);
});
export const simulationDialogOpenSelector = createSelector(getWorkspaceState, (state) => state.simulationDialogOpen);
export const insightDialogOpenSelector = createSelector(getWorkspaceState, (state) => state.insightDialogOpen);
export const optimizationDialogOpenSelector = createSelector(getWorkspaceState, (state) => state.optimizationDialogOpen);
export const workspaceCellModelsAvailableForAddSelector = createSelector(fullyConfiguredCellModelsSelector, workspaceCellModelsSelector, (fullyConfiguredCellModels, workspaceCellModels) => fullyConfiguredCellModels.filter(({ id, refined, refinementStatus }) => (!refined || (refined && checkRefinementIsCalculated(refinementStatus))) &&
    !workspaceCellModels.find((item) => item.id === id)));
export const workspaceRecipesAvailableForAddSelector = createSelector(fullyConfiguredRecipesSelector, workspaceRecipesSelector, (fullyConfiguredRecipes, workspaceRecipes) => fullyConfiguredRecipes.filter((recipe) => !workspaceRecipes.find((item) => item.id === recipe.id)));
export const workspaceProcessDataAvailableForAddSelector = createSelector(processDataListSelector, workspaceProcessDataSelector, (processDataItems, workspaceProcessData) => processDataItems.filter((processDataItem) => !workspaceProcessData.find((item) => item.id === processDataItem.id)));
export const recipeOptionsAvailableForSimulationSelector = (currentRecipeId) => createSelector(workspaceRecipesSelector, (recipes) => recipes.reduce((acc, { isCompleted, id, name }) => {
    if (isCompleted || id === currentRecipeId) {
        acc.push({ value: id, label: name, disabled: !isCompleted && id === currentRecipeId });
    }
    return acc;
}, []));
export const cellModelOptionsAvailableForSimulationSelector = (currentCellModelId) => createSelector(workspaceCellModelsSelector, (cellModels) => cellModels.reduce((acc, { isCompleted, id, name }) => {
    if (isCompleted || id === currentCellModelId) {
        acc.push({ value: id, label: name, disabled: !isCompleted && id === currentCellModelId });
    }
    return acc;
}, []));
export const addSimulationButtonHighlightedSelector = createSelector(workspaceSimulationsSelector, workspaceCellModelsSelector, workspaceRecipesSelector, (simulations, cellModels, recipes) => simulations.length === 0 && cellModels.length > 0 && recipes.length > 0);
export const addOptimizationButtonHighlightedSelector = createSelector(workspaceSimulationsSelector, workspaceCellModelsSelector, workspaceRecipesSelector, workspaceOptimizationsSelector, workspaceInsightsSelector, (simulations, cellModels, recipes, optimizations, insights) => optimizations.length === 0 &&
    cellModels.length > 0 &&
    recipes.length > 0 &&
    simulations.length > 0 &&
    insights.length > 0);
export const addInsightButtonHighlightedSelector = createSelector(workspaceCellModelsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, workspaceInsightsSelector, (cellModels, recipes, simulations, insights) => cellModels.length > 0 && recipes.length > 0 && simulations.length > 0 && insights.length === 0);
export const availableScenarioHeightSelector = createSelector(workspaceCellModelsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, workspaceInsightsSelector, workspaceOptimizationsSelector, (cellModels, recipes, simulations, insights, optimizations) => calculateAvailableHeight(cellModels, recipes, simulations, insights, optimizations));
export const scenarioCanvasDataSelector = (widgetWidth) => createSelector(workspaceCellModelsSelector, workspaceRecipesSelector, workspaceSimulationsSelector, workspaceInsightsSelector, workspaceOptimizationsSelector, workspaceProcessDataSelector, availableScenarioHeightSelector, highlightLeadingNodeSelector, processVariablesSelector, incompleteSimulationsIdsSelector, (cellModels, recipes, simulations, insights, optimizations, processData, availableHeight, highlightLeadingNode, processVaribales, incompleteSimulationsIds) => positionScenarioCanvasElements(cellModels, recipes, simulations, insights, optimizations, processData, widgetWidth, availableHeight, highlightLeadingNode, processVaribales, incompleteSimulationsIds));
export const scenarioCanvasCellModelsRecipesSectionHeightSelector = createSelector(workspaceCellModelsSelector, workspaceRecipesSelector, availableScenarioHeightSelector, (cellModels, recipes, availableHeight) => calculateScenarioCellModelsRecipesSectionHeight(cellModels, recipes, availableHeight));
export const scenarioCanvasAddCellModelButtonPositionSelector = (widgetWidth) => createSelector(workspaceCellModelsSelector, workspaceRecipesSelector, availableScenarioHeightSelector, (cellModels, recipes, availableHeight) => {
    const positionedCellModels = positionScenarioCellModels(cellModels, recipes, widgetWidth, availableHeight, []);
    const flatBottomPositions = positionedCellModels.map((item) => item.data?.bottomYPos ?? 0);
    const lastBottomYPos = positionedCellModels.length > 0 ? Math.max(...flatBottomPositions) : 0;
    return lastBottomYPos + SECTION_SUBHEADER_HEIGHT;
});
export const scenarioCanvasAddRecipeButtonPositionSelector = (widgetWidth) => createSelector(workspaceRecipesSelector, workspaceCellModelsSelector, availableScenarioHeightSelector, (recipes, cellModels, availableHeight) => {
    const positionedRecipes = positionScenarioRecipes(recipes, cellModels, widgetWidth, availableHeight, []);
    const flatBottomPositions = positionedRecipes.map((item) => item.data?.bottomYPos ?? 0);
    const { cellModelsSectionHeight } = calculateScenarioCellModelsRecipesSectionHeight(cellModels, recipes, availableHeight);
    const lastBottomYPos = positionedRecipes.length > 0
        ? Math.max(...flatBottomPositions)
        : cellModelsSectionHeight + SECTION_SUBHEADER_HEIGHT;
    return lastBottomYPos + SECTION_SUBHEADER_HEIGHT;
});
export const simulationDefaultNameSelector = createSelector(getWorkspaceState, (state) => state.simulationDefaultName);
export const simulationDialogInitialValuesSelector = createSelector(simulationDefaultNameSelector, workspaceCurrentSimulationSelector, workspaceRecipesSelector, workspaceCellModelsSelector, bioreactorsListSelector, workspaceCurrentVariantIdSelector, (simulationDefaultName, currentSimulation, recipes, cellModels, bioreactors, currentVariantId) => {
    const recipe = currentSimulation?.recipeId
        ? recipes.find((item) => item.id === currentSimulation.recipeId)
        : null;
    const cellModel = currentSimulation?.cellModelId
        ? cellModels.find((item) => item.id === currentSimulation.cellModelId)
        : null;
    const bioreactor = currentSimulation?.state.bioreactorId
        ? bioreactors.find((item) => item.id === currentSimulation.state.bioreactorId)
        : null;
    const spargerId = currentSimulation?.state.spargerId;
    const impellerId = currentSimulation?.state.impellerId;
    const variants = currentSimulation?.state.adjustments?.map(({ recipeParameters, cellModelParameters, ...adjustment }) => ({
        ...adjustment,
        recipeParameters: recipeParameters?.map((item) => ({
            ...item,
            [SimulationFormFields._selectedParameterId]: item.id,
        })) ?? [],
        cellModelParameters: cellModelParameters?.map((item) => ({
            parameter: {
                ...item.parameter,
                [SimulationFormFields._selectedParameterId]: item.parameter?.parameterId,
            },
        })) ?? [],
    }));
    const selectedSparger = bioreactor?.permittedSpargers.find((item) => item.id === spargerId);
    const selectedImpeller = bioreactor?.permittedImpellers.find((item) => item.id === impellerId);
    const recipeOption = recipe ? { value: recipe.id, label: recipe.name } : null;
    const cellModelOption = cellModel ? { value: cellModel.id, label: cellModel.name } : null;
    const bioreactorOption = bioreactor ? { value: bioreactor.id, label: bioreactor.name } : null;
    const spargerOption = bioreactor
        ? {
            value: spargerId ? selectedSparger?.id : bioreactor.defaultSparger?.id,
            label: spargerId ? selectedSparger?.name : bioreactor.defaultSparger?.name,
        }
        : null;
    const impellerOption = bioreactor
        ? {
            value: impellerId ? selectedImpeller?.id : bioreactor.defaultImpeller?.id,
            label: impellerId ? selectedImpeller?.name : bioreactor.defaultImpeller?.name,
        }
        : null;
    return {
        [SimulationFormFields.name]: currentSimulation ? currentSimulation.name : simulationDefaultName,
        [SimulationFormFields.recipe]: recipeOption,
        [SimulationFormFields.cellModel]: cellModelOption,
        [SimulationFormFields.bioreactor]: bioreactorOption,
        [SimulationFormFields.sparger]: spargerOption,
        [SimulationFormFields.impeller]: impellerOption,
        [SimulationFormFields.variants]: variants ?? [],
        [SimulationFormFields._adjustmentId]: currentVariantId !== 0 ? currentVariantId : 0,
        [SimulationFormFields.variables]: [],
    };
});
export const fetchingSimulationPreviewDataSelector = (simulationId) => createSelector(previewRequestsSelector, (previewRequests) => previewRequests.some(({ data, type }) => type === InvokeRsSocketMethods.CALCULATE_SIMULATION_PREVIEW && data.simulationId === simulationId));
export const simulationByIdSelector = (simulationId) => createSelector(workspaceSimulationsSelector, (simulations) => simulations.find((item) => item.id === simulationId));
export const processDataByIdSelector = (processDataId) => createSelector(workspaceProcessDataSelector, (processData) => processData.find((item) => item.id === processDataId));
export const lockedDialogDataSelector = createSelector(getWorkspaceState, (state) => state.lockedDialogData);
export const isScenarioItemUsedInSimulationSelector = (id, type) => createSelector(workspaceSimulationsSelector, (simulations) => !!simulations.find(({ cellModelId, recipeId }) => {
    switch (type) {
        case ScenarioNodeType.CELLMODEL:
            return cellModelId === id;
        case ScenarioNodeType.RECIPE:
            return recipeId === id;
    }
    return false;
}));
export const isVariantUsedSelector = (variantId) => createSelector(getWorkspaceStateSelector, workspaceCurrentSimulationIdSelector, (state, currentSimulationId) => {
    const isUsedInResult = isSimulationVariantUsedInResultElement(state.resultSets, currentSimulationId, variantId);
    const isUsedInInsight = isVariantUsedInInsight(state.insights ?? [], currentSimulationId, variantId);
    return isUsedInInsight || isUsedInResult;
});
export const persistedOptimizationPreviewsSelector = (id) => createSelector(getWorkspaceState, (state) => isNotEmpty(id) ? state.optimizationPreviews.persisted[id] : undefined);
export const temporaryOptimizationPreviewSelector = createSelector(getWorkspaceState, (state) => state.optimizationPreviews.temporary ?? {});
export const optimizationPreviewSelector = createSelector(getWorkspaceState, (state) => state.optimizationPreviews);
export const optimizationPreviewDataSelector = (optimizationParameters, optimizationTarget, insightId, insightInputSetId, sourceCompleted) => createSelector(temporaryOptimizationPreviewSelector, workspaceSimulationsSelector, workspaceCellModelsSelector, workspaceRecipesSelector, workspaceInsightsSelector, (temporaryPreview, simulations, cellModels, recipes, insights) => {
    const { result, requestedParameters = [], selectedAxisIndexes } = temporaryPreview;
    const targets = optimizationTarget === OptimizationTargetType.CELL_MODEL ? cellModels : recipes;
    const sourceType = optimizationTarget === OptimizationTargetType.CELL_MODEL
        ? GlobalEntityType.CellModel
        : GlobalEntityType.Recipe;
    const selectedInsight = insights.find(({ id }) => id === insightId);
    const parameters = optimizationParameters.reduce((acc, { parameters, targetSource, inputId }) => {
        const sectionTargetSource = simulations.find(({ id }) => id === targetSource?.sourceId);
        const sectionTargetId = optimizationTarget === OptimizationTargetType.CELL_MODEL
            ? sectionTargetSource?.cellModelId
            : sectionTargetSource?.recipeId;
        const selectedInsightInput = selectedInsight?.state?.input?.inputSets
            ?.find(({ id }) => id === insightInputSetId)
            ?.inputs?.find(({ id }) => id === inputId);
        const target = targets.find(({ id }) => id === sectionTargetId);
        parameters?.forEach((parameter) => {
            switch (parameter.type) {
                case OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE:
                    if (Number(parameter.max) > Number(parameter.min)) {
                        const { parameterId, min, max } = parameter;
                        if (target && optimizationTarget && sectionTargetId && targetSource) {
                            const { name = "", value: parameterValue } = {
                                ...getSelectedParameterToOptimize(parameter, target, optimizationTarget, sectionTargetSource?.parametersForVariants?.[targetSource.adjustmentId]),
                            };
                            if (isNotEmpty(parameterValue, true)) {
                                acc.push({
                                    id: parameterId,
                                    max: Number(max),
                                    min: Number(min),
                                    name: `${selectedInsightInput?.id}. ${name}`,
                                    originalName: name,
                                    sourceId: sectionTargetId,
                                    simulationName: sectionTargetSource?.name,
                                    inputId,
                                    sourceType,
                                    type: SpaceviewerAxisType.FLOAT,
                                });
                            }
                        }
                    }
                    break;
                case OptimizationParameterParameterType.ALTERNATIVE_FLOW: {
                    const { stepId, flowIds = [] } = parameter;
                    if (target) {
                        const { name = "", flowProperties = [] } = {
                            ...flatSteps(target.state.steps || []).find(({ id }) => id === stepId),
                        };
                        const sortedFlowIds = [...flowIds].sort((a, b) => a - b);
                        const steps = sortedFlowIds.map((flowId, index) => {
                            const flowIndex = flowProperties.findIndex(({ id }) => id === flowId);
                            return {
                                label: getAlternativeStepFlowName(flowProperties[flowIndex] || {}, flowIndex),
                                value: index,
                            };
                        });
                        if ((steps?.length ?? 0) >= MIN_FLOWS_QUANTITY && sectionTargetId !== undefined) {
                            acc.push({
                                id: stepId,
                                name: `${selectedInsightInput?.id}. ${name}`,
                                originalName: name,
                                sourceType,
                                sourceId: sectionTargetId,
                                simulationName: sectionTargetSource?.name,
                                inputId,
                                simulationId: sectionTargetSource?.id,
                                adjustmentId: targetSource?.adjustmentId,
                                type: SpaceviewerAxisType.INTEGER,
                                steps,
                                flowIds: sortedFlowIds,
                            });
                        }
                    }
                }
            }
        });
        return acc;
    }, []);
    const aggregatedData = mergeSpaceviewerResults(result?.data ?? [], parameters);
    const { value, values = [] } = aggregatedData;
    const data = {
        parameters,
        values,
        value,
        sourceCompleted,
    };
    return { data, requestedParameters, selectedAxisIndexes, errorMessage: result?.errorMessage };
});
export const fetchingCurrentOptimizationPreviewDataSelector = createSelector(previewRequestsSelector, workspaceCurrentOptimizationIdSelector, (previewRequests, currentOptimizationId) => !!previewRequests.find(({ data, type }) => {
    if (type === InvokeRsSocketMethods.CALCULATE_OPTIMIZATION_PREVIEW) {
        return data.elementId === (currentOptimizationId === null ? -1 : currentOptimizationId);
    }
}));
export const getInsightInputOptionsSelector = (insightId, inputSetId) => createSelector(workspaceInsightsSelector, processVariablesSelector, incompleteSimulationsIdsSelector, workspaceSimulationsSelector, workspaceCellModelsSelector, workspaceRecipesSelector, (workspaceInsights, processVariables, incompleteSimulationIds, simulations, cellModels, recipes) => getInsightInputSetOptions(insightId, inputSetId, workspaceInsights, processVariables, incompleteSimulationIds, simulations, cellModels, recipes));
export const getInsightOutputOptionsSelector = (insightId, inputSetId, outputId) => createSelector(workspaceInsightsSelector, persistedInsightsDataSelector, processVariablesSelector, workspaceRecipesSelector, (workspaceInsights, persistedInsightsData, processVariables, recipes) => getInsightOutputOptions(insightId, inputSetId, outputId, workspaceInsights, persistedInsightsData, processVariables, recipes));
export const fetchingOptimizationPreviewDataSelector = (optimizationId) => createSelector(previewRequestsSelector, (previewRequests) => !!previewRequests.find(({ data, type }) => {
    if (type === InvokeRsSocketMethods.CALCULATE_OPTIMIZATION_PREVIEW) {
        return optimizationId === null ? false : data.elementId === optimizationId;
    }
}));
export const oldInsightValueForOptimizationSelector = (input) => createSelector(workspaceInsightsSelector, persistedInsightsDataSelector, (workspaceInsights, persistedInsightsData) => {
    const { insightId, outputId, insightInputSetId } = input ?? {};
    if (isNotEmpty(insightId) && isNotEmpty(outputId)) {
        const currentInsight = workspaceInsights.find(({ id }) => id === insightId);
        const currentInsightPersistedData = persistedInsightsData.find((data) => data.insightId === insightId);
        if (!currentInsight || !currentInsightPersistedData) {
            return "";
        }
        const outputScore = currentInsightPersistedData.scores?.find((score) => score.inputSetId === insightInputSetId && score.outputId === outputId);
        if (outputId === OUTPUT_FINAL_ID) {
            const finalScore = calculateInputSetFinalScore(insightId, insightInputSetId, persistedInsightsData);
            const outputsWithScoresIds = currentInsightPersistedData.scores
                ?.filter(({ inputSetId }) => inputSetId === insightInputSetId)
                ?.map(({ outputId }) => outputId);
            const allScoresAvailable = currentInsight.state.outputs.length === outputsWithScoresIds?.length;
            return allScoresAvailable ? (finalScore ?? N_A) : N_A;
        }
        else {
            return outputScore?.score.data?.advancedResult ?? outputScore?.score.data?.result ?? N_A;
        }
    }
    return "";
});
export const oldOptimizationValuesSelector = (state) => createSelector(workspaceInsightsSelector, workspaceSimulationsSelector, workspaceRecipesSelector, workspaceCellModelsSelector, (insights, simulations, recipes, cellModels) => {
    const { insightId, optimizationTarget: target, insightInputSetId } = state?.input ?? {};
    if (!isNotEmpty(insightId) || !isNotEmpty(insightInputSetId)) {
        return [];
    }
    const selectedInsight = insights.find(({ id }) => id === insightId);
    const { parameters = [] } = state || {};
    return parameters
        .map(({ parameters, inputId, targetSource }) => {
        const selectedInput = selectedInsight?.state?.input?.inputSets
            ?.find(({ id }) => id === insightInputSetId)
            ?.inputs?.find(({ id }) => id === inputId);
        if (!isNotEmpty(selectedInput) || selectedInput?.inputType !== InsightInputType.SIMULATION) {
            return [];
        }
        const { sourceId, adjustmentId } = { ...targetSource };
        const selectedSimulation = simulations.find(({ id }) => id === sourceId);
        const parametersOverrides = adjustmentId !== undefined && adjustmentId !== 0
            ? selectedSimulation?.parametersForVariants?.[adjustmentId]
            : undefined;
        switch (target) {
            case OptimizationTargetType.RECIPE: {
                const target = recipes.find(({ id }) => id === selectedSimulation?.recipeId) || {};
                return parameters?.map((optimizationParameter) => {
                    switch (optimizationParameter.type) {
                        case OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE: {
                            const { parameterId } = optimizationParameter;
                            const parameter = getSelectedRecipeParameterToOptimize(target, parametersOverrides, parameterId);
                            return {
                                parameterId,
                                value: parameter?.value,
                                inputId: selectedInput.id,
                                sourceId: target?.id,
                                source: target?.name,
                                type: SpaceviewerAxisType.FLOAT,
                                unit: parameter?.units,
                            };
                        }
                        case OptimizationParameterParameterType.ALTERNATIVE_FLOW: {
                            const { stepId } = optimizationParameter;
                            const step = flatSteps(target?.state.steps ?? []).find(({ id }) => `${stepId}` === `${id}`);
                            const usedVariant = adjustmentId !== undefined && adjustmentId !== 0
                                ? selectedSimulation?.state.adjustments?.find(({ id }) => id === selectedInput.adjustmentId)
                                : undefined;
                            const usedFlowId = usedVariant?.flowConfig?.stepsConfig?.find((config) => stepId === config.stepId)?.flowId;
                            const usedFlowIndex = (step?.flowProperties || []).findIndex(({ id, isDefault }) => isNotEmpty(usedFlowId) ? id === usedFlowId : isDefault);
                            return {
                                stepId,
                                inputId: selectedInput.id,
                                source: target?.name,
                                sourceId: target?.id,
                                flowIds: step?.flowProperties.map(({ id }) => id) ?? [],
                                type: SpaceviewerAxisType.INTEGER,
                                value: getAlternativeStepFlowName(step?.flowProperties[usedFlowIndex] || {}, usedFlowIndex),
                            };
                        }
                    }
                });
            }
            case OptimizationTargetType.CELL_MODEL: {
                const target = cellModels.find(({ id }) => id === selectedSimulation?.cellModelId) || {};
                return parameters
                    ?.filter(({ type }) => type === OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE)
                    .map((optimizationParameter) => {
                    const { parameterId } = optimizationParameter;
                    const parameter = getSelectedCellModelParameterToOptimize(target, parametersOverrides, parameterId);
                    return {
                        parameterId,
                        value: parameter?.value,
                        inputId: selectedInput.id,
                        source: target?.name,
                        sourceId: target?.id,
                        type: SpaceviewerAxisType.FLOAT,
                        unit: parameter?.units,
                    };
                });
            }
        }
    })
        .flat();
});
export const optimizationPreviewInsightErrorSelector = createSelector(workspaceInsightsSelector, processVariablesSelector, incompleteSimulationsIdsSelector, workspaceSimulationsSelector, workspaceCellModelsSelector, workspaceRecipesSelector, (workspaceInsights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes) => workspaceInsights.some((el) => isInsightCompleted(el.state, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes) === false));
export const hasInsightUncompletedSimulationsSelector = (id) => createSelector(insightByIdSelector(id), incompleteSimulationsIdsSelector, (insight, uncompletedSimulationsIds) => {
    if (!insight) {
        return false;
    }
    const { inputSets } = insight.state.input;
    let hasUncompleteUsedSimulation = false;
    const allSourcesIds = insight.state.outputs.reduce((acc, outputItem) => {
        const { sourceA, sourceB } = outputItem;
        if (outputItem.type === InsightOutputTypes.Comparison) {
            return [...acc, sourceA, sourceB];
        }
        return [...acc, sourceA];
    }, []);
    for (const inputSet of inputSets) {
        const isUncompleteSimulationUsed = inputSet.inputs
            .filter((el) => allSourcesIds.includes(el.id))
            .some((input) => input.inputId !== null && uncompletedSimulationsIds.includes(input.inputId));
        if (isUncompleteSimulationUsed) {
            hasUncompleteUsedSimulation = true;
        }
    }
    return hasUncompleteUsedSimulation;
});
export const filteredOptimizationParametersToCalculateSelector = (optimizationState) => createSelector(workspaceSimulationsSelector, workspaceCellModelsSelector, workspaceRecipesSelector, (simulations, cellModels, recipes) => {
    const { input, parameters } = { ...optimizationState };
    const { optimizationTarget } = { ...input };
    return (parameters?.reduce((acc, parametersSection) => {
        const sectionTargetSource = simulations.find(({ id }) => id === parametersSection?.targetSource?.sourceId);
        const sectionTargetId = optimizationTarget === OptimizationTargetType.CELL_MODEL
            ? sectionTargetSource?.cellModelId
            : sectionTargetSource?.recipeId;
        const filteredParametersSection = parametersSection.parameters?.reduce((filteredSection, parameter) => {
            switch (parameter?.type) {
                case OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE: {
                    const { max, min } = parameter;
                    if (!isNotEmpty(min) || !isNotEmpty(max) || Number(max) <= Number(min)) {
                        return filteredSection;
                    }
                    const target = optimizationTarget === OptimizationTargetType.RECIPE
                        ? recipes.find(({ id }) => id === sectionTargetId)
                        : cellModels.find(({ id }) => id === sectionTargetId);
                    if (target && optimizationTarget && parametersSection.targetSource) {
                        const selectedParameter = getSelectedParameterToOptimize(parameter, target, optimizationTarget, sectionTargetSource?.parametersForVariants?.[parametersSection.targetSource.adjustmentId]);
                        if (isNotEmpty(selectedParameter?.value, true)) {
                            filteredSection.push(parameter);
                        }
                    }
                    return filteredSection;
                }
                case OptimizationParameterParameterType.ALTERNATIVE_FLOW: {
                    if (isNotEmpty(parameter.stepId) &&
                        (parameter.flowIds?.length ?? 0) >= MIN_FLOWS_QUANTITY) {
                        const { type, stepId, flowIds, id } = parameter;
                        filteredSection.push({ id, type, stepId, flowIds });
                    }
                    return filteredSection;
                }
                default:
                    return filteredSection;
            }
        }, []) || [];
        if (filteredParametersSection?.length > 0) {
            acc.push({ ...parametersSection, parameters: filteredParametersSection });
        }
        return acc;
    }, []) || []);
});
export const insightsScoreDataFromCompleteSourcesSelector = (simulationId) => createSelector(workspaceSimulationsSelector, workspaceInsightsSelector, workspaceCellModelsSelector, workspaceRecipesSelector, persistedInsightsDataSelector, (simulations, insights, cellModels, recipes, persistedInsightsData) => {
    const relatedInsights = insights.filter(({ state }) => state.input.inputSets.some((item) => item.inputs.some((input) => input.inputType === InsightInputType.SIMULATION && input.inputId === simulationId)));
    return persistedInsightsData.reduce((acc, insightData) => {
        const insight = relatedInsights?.find(({ id }) => id === insightData?.insightId);
        if (insightData.isOriginal || !insight) {
            return [...acc, insightData];
        }
        const scoresFromCompleteSources = insightData?.scores?.filter(({ inputSetId, outputId }) => {
            const sourceInputSet = insight?.state?.input?.inputSets?.find(({ id }) => id === inputSetId);
            const sourceOutput = insight?.state.outputs?.find(({ id }) => id === outputId);
            const sourcesToCheck = sourceOutput?.type === InsightOutputTypes.Evaluation
                ? [sourceOutput?.sourceA]
                : [sourceOutput?.sourceA, sourceOutput?.sourceB];
            const sourceComplete = sourcesToCheck.every((inputId) => {
                const sourceInput = sourceInputSet?.inputs?.find(({ id }) => id === inputId);
                const simulation = simulations?.find(({ id }) => id === sourceInput?.inputId);
                const variant = simulation?.state?.adjustments?.find(({ id }) => id === sourceInput?.adjustmentId);
                if (sourceInput?.inputType === InsightInputType.PROCESS_DATA) {
                    return true;
                }
                return (isSimulationCompleted(simulation, cellModels, recipes) &&
                    isSimulationVariantCompleted(simulation, cellModels, recipes, variant));
            });
            return sourceComplete;
        });
        return [...acc, { ...insightData, scores: scoresFromCompleteSources }];
    }, []);
});
